import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '../config/firebase'

export interface LogoUploadResult {
  url: string
  path: string
}

export class StorageService {
  /**
   * Upload an organization logo to Firebase Storage
   * @param organizationId - The organization ID
   * @param file - The image file to upload
   * @param type - The logo type ('light' or 'dark')
   * @returns Promise with the download URL and storage path
   */
  static async uploadOrganizationLogo(
    organizationId: string,
    file: File,
    type: 'light' | 'dark'
  ): Promise<LogoUploadResult> {
    // Validate file
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image')
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      throw new Error('File size must be less than 5MB')
    }

    // Generate file extension from MIME type
    const extension = this.getFileExtension(file.type)
    
    // Create storage path: organizations/{orgId}/logos/{type}.{ext}
    const storagePath = `organizations/${organizationId}/logos/${type}.${extension}`
    
    try {
      // Create storage reference
      const storageRef = ref(storage, storagePath)
      
      // Upload file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          organizationId,
          logoType: type,
          uploadedAt: new Date().toISOString()
        }
      }
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, metadata)
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref)
      
      return {
        url: downloadURL,
        path: storagePath
      }
    } catch (error) {
      console.error('Logo upload failed:', error)
      throw new Error(`Failed to upload ${type} logo: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Delete an organization logo from Firebase Storage
   * @param storagePath - The storage path of the logo to delete
   */
  static async deleteOrganizationLogo(storagePath: string): Promise<void> {
    try {
      const storageRef = ref(storage, storagePath)
      await deleteObject(storageRef)
    } catch (error) {
      console.error('Logo deletion failed:', error)
      throw new Error(`Failed to delete logo: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get file extension from MIME type
   * @param mimeType - The MIME type of the file
   * @returns The file extension
   */
  private static getFileExtension(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg'
    }
    
    return mimeToExt[mimeType] || 'jpg'
  }

  /**
   * Generate a public URL for an organization logo
   * @param organizationId - The organization ID
   * @param type - The logo type ('light' or 'dark')
   * @param extension - The file extension (optional, defaults to 'png')
   * @returns The storage path for the logo
   */
  static getLogoPath(organizationId: string, type: 'light' | 'dark', extension: string = 'png'): string {
    return `organizations/${organizationId}/logos/${type}.${extension}`
  }

  /**
   * Upload a user profile photo to Firebase Storage
   * @param userId - The user ID
   * @param file - The image file to upload
   * @returns Promise with the download URL and storage path
   */
  static async uploadUserProfilePhoto(
    userId: string,
    file: File
  ): Promise<LogoUploadResult> {
    // Validate file
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image')
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      throw new Error('File size must be less than 5MB')
    }

    // Generate file extension from MIME type
    const extension = this.getFileExtension(file.type)
    
    // Create storage path: users/{userId}/profile.{ext}
    const storagePath = `users/${userId}/profile.${extension}`
    
    try {
      // Create storage reference
      const storageRef = ref(storage, storagePath)
      
      // Upload file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          userId,
          photoType: 'profile',
          uploadedAt: new Date().toISOString()
        }
      }
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, metadata)
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref)
      
      return {
        url: downloadURL,
        path: storagePath
      }
    } catch (error) {
      console.error('Profile photo upload failed:', error)
      throw new Error(`Failed to upload profile photo: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Delete a user profile photo from Firebase Storage
   * @param storagePath - The storage path of the photo to delete
   */
  static async deleteUserProfilePhoto(storagePath: string): Promise<void> {
    try {
      const storageRef = ref(storage, storagePath)
      await deleteObject(storageRef)
    } catch (error) {
      console.error('Profile photo deletion failed:', error)
      throw new Error(`Failed to delete profile photo: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
} 